import { useState } from 'react';
import { useController } from 'react-hook-form';
import { IoCloudUploadOutline, IoTrashOutline } from 'react-icons/io5';

import {
    Box,
    Button,
    ButtonProps,
    FileButton,
    Flex,
    Input,
    InputWrapperProps,
    Loader,
    Space,
    Text,
    UnstyledButton,
} from '@mantine/core';

import { IKImage } from 'components/ik-image/IKImage';
import { useFile } from 'hooks/use-file';
import { FileService } from 'services/FileService';
import { LocalNotificationService } from 'services/LocalNotificationService';

import cx from './ImageField.module.scss';

type ImageFieldProps = InputWrapperProps & {
    name: string;
    group: string;
    buttonLabel: string;
    buttonProps?: ButtonProps;
    showPreview?: boolean;
    onChange?: (id: string) => void;
    type?: 'image' | 'video';
    maxFileSize?: number; // in bytes
};

const ImageField = ({
    name,
    group,
    buttonLabel,
    buttonProps,
    showPreview = true,
    onChange,
    type = 'image',
    maxFileSize,
    ...props
}: ImageFieldProps) => {
    const {
        field: { value, onChange: internalOnChange },
        fieldState: { error },
    } = useController({ name });

    const [uploading, setUploading] = useState(false);
    const { file } = useFile(value);

    const handleUpload = async (file: File) => {
        try {
            if (maxFileSize && file.size > maxFileSize) {
                const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(1);
                const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);

                LocalNotificationService.showError({
                    message: `File size (${fileSizeMB}MB) exceeds maximum allowed size (${maxSizeMB}MB)`,
                });
                return;
            }

            setUploading(true);

            const serverFile = await FileService.create({
                file,
                group,
            });

            onChange?.(serverFile.id);
            internalOnChange(serverFile.id);
        } catch (error) {
            LocalNotificationService.showError({ message: 'Error uploading ' + type });
            console.error(error);
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = () => {
        internalOnChange('');
    };

    const UploadButton = (
        <FileButton
            data-file-button={name}
            onChange={(file) => file && handleUpload(file)}
            accept={type === 'image' ? 'image/*' : 'video/*'}
        >
            {(button) => (
                <Button
                    {...button}
                    variant="default"
                    leftSection={<IoCloudUploadOutline />}
                    loading={uploading}
                    {...buttonProps}
                >
                    {buttonLabel}
                </Button>
            )}
        </FileButton>
    );

    if (!showPreview) {
        return (
            <Flex gap={4}>
                {UploadButton}
                {file && (
                    <Button onClick={handleDelete} {...buttonProps} leftSection="" color="red" c="red">
                        <IoTrashOutline />
                    </Button>
                )}
            </Flex>
        );
    }

    return (
        <Input.Wrapper
            {...props}
            error={
                <Text component="span" inherit mt={4}>
                    {error?.message}
                </Text>
            }
        >
            {uploading || file ? (
                <Box className={cx.wrapper}>
                    {uploading && <Loader size="xs" color="dimmed" />}
                    {file && (
                        <>
                            {type === 'image' && (
                                <>
                                    <IKImage fileOrId={file} width={200} fit="cover" alt="" className={cx.image} />
                                    <UnstyledButton className={cx.delete} onClick={handleDelete}>
                                        <IoTrashOutline size={20} />
                                    </UnstyledButton>
                                </>
                            )}
                            {type === 'video' && (
                                <>
                                    <video
                                        className={cx.image}
                                        width={200}
                                        height={200}
                                        src={file.url}
                                        muted
                                        autoPlay
                                        playsInline
                                        disablePictureInPicture
                                        controlsList="nofullscreen nodownload noremoteplayback noplaybackrate"
                                    />
                                    <UnstyledButton className={cx.delete} onClick={handleDelete}>
                                        <IoTrashOutline size={20} />
                                    </UnstyledButton>
                                </>
                            )}
                        </>
                    )}
                </Box>
            ) : (
                <Box>
                    <Space h="4" />
                    {UploadButton}
                </Box>
            )}
        </Input.Wrapper>
    );
};

export { ImageField };
export { ImageField as VideoField };
